'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import { Calendar, Car, Star, CreditCard, Gift, Clock, MapPin, Truck, Plus, MessageCircle, RotateCcw, ClipboardList } from 'lucide-react';

const upcomingAppointment = {
  id: 'apt-0421',
  service: 'Outside + Inside Wash',
  date: '01 May 2026',
  time: '10:45',
  type: 'mobile',
  address: 'Kaya Flamboyan 12, Willemstad',
  status: 'confirmed',
  employee: 'Jayden Rosario',
  price: 50,
  vehicle: 'Toyota Corolla · ABC-1234',
};

const appointmentHistory = [
  { id: 'apt-0418', date: '25 Apr 2026', service: 'Outside Wash Only', status: 'completed', price: 30, vehicle: 'ABC-1234', type: 'location', loyalty: true },
  { id: 'apt-0412', date: '18 Apr 2026', service: 'Outside + Inside Wash', status: 'completed', price: 50, vehicle: 'ABC-1234', type: 'mobile', loyalty: true },
  { id: 'apt-0405', date: '11 Apr 2026', service: 'Tire Shine Add-On', status: 'completed', price: 10, vehicle: 'ABC-1234', type: 'location', loyalty: false },
  { id: 'apt-0398', date: '04 Apr 2026', service: 'Outside + Inside Wash', status: 'completed', price: 50, vehicle: 'ABC-1234', type: 'mobile', loyalty: true },
  { id: 'apt-0385', date: '28 Mar 2026', service: 'Full Interior Detail', status: 'completed', price: 125, vehicle: 'DEF-5678', type: 'location', loyalty: false },
  { id: 'apt-0371', date: '21 Mar 2026', service: 'Outside Wash Only', status: 'cancelled', price: 30, vehicle: 'ABC-1234', type: 'location', loyalty: false },
];

const vehicles = [
  { id: 'veh-001', plate: 'ABC-1234', brand: 'Toyota', model: 'Corolla', color: 'Silver', size: 'Medium', lastWash: '25 Apr 2026', totalWashes: 12 },
  { id: 'veh-002', plate: 'DEF-5678', brand: 'Mitsubishi', model: 'Outlander', color: 'Black', size: 'Large / SUV', lastWash: '28 Mar 2026', totalWashes: 4 },
];

const paymentHistory = [
  { id: 'pay-0421', date: '25 Apr 2026', amount: 30, method: 'Sentoo', status: 'paid', ref: 'SNT-8821' },
  { id: 'pay-0418', date: '18 Apr 2026', amount: 50, method: 'Card', status: 'paid', ref: 'CRD-4410' },
  { id: 'pay-0412', date: '11 Apr 2026', amount: 10, method: 'Cash', status: 'paid', ref: 'CSH-0311' },
  { id: 'pay-0405', date: '04 Apr 2026', amount: 50, method: 'Sentoo', status: 'paid', ref: 'SNT-7734' },
  { id: 'pay-0398', date: '28 Mar 2026', amount: 125, method: 'Online Banking', status: 'paid', ref: 'BNK-2290' },
];

const LOYALTY_STAMPS = 3;
const LOYALTY_GOAL = 4;

export default function CustomerDashboardContent() {
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'vehicles' | 'payments'>('overview');

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: LayoutDashboard2 },
    { id: 'history' as const, label: 'History', icon: ClipboardList },
    { id: 'vehicles' as const, label: 'Vehicles', icon: Car },
    { id: 'payments' as const, label: 'Payments', icon: CreditCard },
  ];

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-extrabold text-dark">Good morning, Keisha 👋</h1>
          <p className="text-sm text-muted-foreground mt-1">Wednesday, 30 April 2026</p>
        </div>
        <div className="flex gap-3">
          <a
            href="https://wa.me/5999placeholder"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition-colors"
          >
            <MessageCircle size={16} />
            WhatsApp
          </a>
          <Link href="/book-appointment" className="btn-primary text-sm px-5 py-2.5">
            <Plus size={16} />
            Book Wash
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 2xl:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
              <Calendar size={15} className="text-primary" />
            </div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Next Wash</span>
          </div>
          <p className="text-lg font-extrabold text-dark tabular-nums">01 May</p>
          <p className="text-xs text-muted-foreground">10:45 AM · Mobile</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <Gift size={15} className="text-amber-600" />
            </div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Loyalty</span>
          </div>
          <p className="text-lg font-extrabold text-dark tabular-nums">{LOYALTY_STAMPS}/4</p>
          <p className="text-xs text-muted-foreground">1 more for free wash</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
              <Star size={15} className="text-violet-600" />
            </div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Subscription</span>
          </div>
          <p className="text-lg font-extrabold text-dark">Inactive</p>
          <p className="text-xs text-primary font-medium cursor-pointer hover:underline">Activate 250 XCG/mo</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <Car size={15} className="text-green-600" />
            </div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vehicles</span>
          </div>
          <p className="text-lg font-extrabold text-dark tabular-nums">2</p>
          <p className="text-xs text-muted-foreground">Registered vehicles</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-sky-50 rounded-2xl p-1 mb-6 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: ClipboardList },
          { id: 'history', label: 'History', icon: ClipboardList },
          { id: 'vehicles', label: 'Vehicles', icon: Car },
          { id: 'payments', label: 'Payments', icon: CreditCard },
        ].map((t) => (
          <button
            key={`cdash-tab-${t.id}`}
            onClick={() => setActiveTab(t.id as typeof activeTab)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
              activeTab === t.id ? 'bg-white text-dark shadow-sm' : 'text-muted-foreground hover:text-dark'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-3 2xl:grid-cols-3 gap-6">
          {/* Upcoming appointment */}
          <div className="lg:col-span-2">
            <h2 className="text-base font-bold text-dark mb-4">Upcoming Appointment</h2>
            <div className="bg-white rounded-2xl border-2 border-primary shadow-card p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="badge badge-confirmed mb-2">Confirmed</span>
                  <h3 className="font-extrabold text-dark text-lg">{upcomingAppointment.service}</h3>
                  <p className="text-sm text-muted-foreground">{upcomingAppointment.vehicle}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-extrabold text-primary tabular-nums">{upcomingAppointment.price} XCG</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Calendar size={15} className="text-primary" />
                  {upcomingAppointment.date} at {upcomingAppointment.time}
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Truck size={15} className="text-emerald-500" />
                  Mobile Van Service
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <MapPin size={15} className="text-primary" />
                  {upcomingAppointment.address}
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Clock size={15} className="text-primary" />
                  Employee: {upcomingAppointment.employee}
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => toast.info('Reschedule request sent to manager')}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border border-sky-200 text-sm font-semibold text-slate-700 hover:bg-sky-50 transition-colors"
                >
                  <RotateCcw size={14} />
                  Reschedule
                </button>
                <a
                  href="https://wa.me/5999placeholder"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500 text-white text-sm font-semibold hover:bg-green-600 transition-colors"
                >
                  <MessageCircle size={14} />
                  Contact via WhatsApp
                </a>
              </div>
            </div>

            {/* Recent activity */}
            <h2 className="text-base font-bold text-dark mt-6 mb-4">Recent Appointments</h2>
            <div className="bg-white rounded-2xl border border-sky-100 shadow-card overflow-hidden">
              {appointmentHistory.slice(0, 4).map((apt, i) => (
                <div key={apt.id} className={`flex items-center gap-4 px-5 py-4 hover:bg-sky-50/50 transition-colors ${i < 3 ? 'border-b border-sky-50' : ''}`}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    apt.type === 'mobile' ? 'bg-emerald-100' : 'bg-sky-100'
                  }`}>
                    {apt.type === 'mobile' ? <Truck size={16} className="text-emerald-600" /> : <MapPin size={16} className="text-primary" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-dark truncate">{apt.service}</p>
                    <p className="text-xs text-muted-foreground">{apt.date} · {apt.vehicle}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`badge ${apt.status === 'completed' ? 'badge-completed' : 'badge-cancelled'} mb-1`}>
                      {apt.status}
                    </span>
                    <p className="text-xs font-bold text-primary tabular-nums">{apt.price} XCG</p>
                  </div>
                  {apt.loyalty && (
                    <div title="Loyalty stamp earned" className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs">💧</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right sidebar */}
          <div className="space-y-6">
            {/* Loyalty card */}
            <div className="bg-white rounded-2xl border border-sky-100 shadow-card p-5">
              <div className="flex items-center gap-2 mb-4">
                <Gift size={18} className="text-amber-500" />
                <h3 className="font-bold text-dark">Loyalty Rewards</h3>
              </div>
              <div className="flex justify-between text-sm font-semibold text-slate-600 mb-2">
                <span>{LOYALTY_STAMPS} of {LOYALTY_GOAL} washes</span>
                <span className="text-primary">{Math.round((LOYALTY_STAMPS / LOYALTY_GOAL) * 100)}%</span>
              </div>
              <div className="h-2.5 bg-sky-100 rounded-full overflow-hidden mb-4">
                <div
                  className="h-full rounded-full bg-gradient-primary"
                  style={{ width: `${(LOYALTY_STAMPS / LOYALTY_GOAL) * 100}%` }}
                />
              </div>
              <div className="flex gap-2 justify-center mb-4">
                {[1, 2, 3, 4].map((s) => (
                  <div
                    key={`loyalty-stamp-${s}`}
                    className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${
                      s <= LOYALTY_STAMPS ? 'bg-gradient-primary shadow-glow' : 'bg-sky-50 border-2 border-dashed border-sky-200'
                    }`}
                  >
                    {s <= LOYALTY_STAMPS ? '💧' : '○'}
                  </div>
                ))}
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl bg-amber-50 border-2 border-dashed border-amber-200">
                  🎁
                </div>
              </div>
              <p className="text-xs text-center text-muted-foreground">
                1 more paid wash to unlock your <strong className="text-primary">FREE wash</strong>
              </p>
            </div>

            {/* Subscription card */}
            <div className="bg-white rounded-2xl border border-sky-100 shadow-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Star size={18} className="text-violet-500" />
                  <h3 className="font-bold text-dark">Subscription</h3>
                </div>
                <span className="badge bg-gray-100 text-gray-500">Inactive</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Get 4 washes per month including inside, outside & tire shine for just 250 XCG.
              </p>
              <Link href="/book-appointment" className="btn-primary w-full justify-center text-sm py-2.5">
                Subscribe — 250 XCG/mo
              </Link>
            </div>

            {/* Vehicles quick view */}
            <div className="bg-white rounded-2xl border border-sky-100 shadow-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Car size={18} className="text-primary" />
                  <h3 className="font-bold text-dark">My Vehicles</h3>
                </div>
                <button onClick={() => setActiveTab('vehicles')} className="text-xs text-primary font-semibold hover:underline">View all</button>
              </div>
              <div className="space-y-3">
                {vehicles.map((v) => (
                  <div key={v.id} className="flex items-center gap-3 p-3 rounded-xl bg-sky-50/50">
                    <div className="w-9 h-9 rounded-lg bg-gradient-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {v.brand.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-dark">{v.brand} {v.model}</p>
                      <p className="text-xs text-muted-foreground font-mono">{v.plate} · {v.color}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-2xl border border-sky-100 shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-sky-50">
            <h2 className="font-bold text-dark">Appointment History</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-sky-50/60">
                  {['Date', 'Service', 'Vehicle', 'Type', 'Status', 'Loyalty', 'Price'].map((h) => (
                    <th key={`hist-h-${h}`} className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {appointmentHistory.map((apt, i) => (
                  <tr key={apt.id} className={`hover:bg-sky-50/50 transition-colors ${i < appointmentHistory.length - 1 ? 'border-b border-sky-50' : ''}`}>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{apt.date}</td>
                    <td className="px-4 py-3 font-semibold text-dark">{apt.service}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{apt.vehicle}</td>
                    <td className="px-4 py-3">
                      <span className={`badge ${apt.type === 'mobile' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'}`}>
                        {apt.type === 'mobile' ? '🚐 Mobile' : '📍 Location'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${apt.status === 'completed' ? 'badge-completed' : 'badge-cancelled'}`}>
                        {apt.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">{apt.loyalty ? '💧' : '—'}</td>
                    <td className="px-4 py-3 font-extrabold text-primary tabular-nums">{apt.price} XCG</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* VEHICLES TAB */}
      {activeTab === 'vehicles' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-dark">Registered Vehicles</h2>
            <button
              onClick={() => toast.info('Vehicle registration form coming soon')}
              className="btn-primary text-sm px-4 py-2"
            >
              <Plus size={14} />
              Add Vehicle
            </button>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
            {vehicles.map((v) => (
              <div key={v.id} className="bg-white rounded-2xl border border-sky-100 shadow-card p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-primary flex items-center justify-center text-white font-extrabold text-lg">
                    {v.brand.charAt(0)}
                  </div>
                  <div>
                    <p className="font-extrabold text-dark">{v.brand} {v.model}</p>
                    <p className="text-xs font-mono text-muted-foreground">{v.plate}</p>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  {[
                    { label: 'Color', value: v.color },
                    { label: 'Size', value: v.size },
                    { label: 'Last Wash', value: v.lastWash },
                    { label: 'Total Washes', value: `${v.totalWashes} washes` },
                  ].map((row) => (
                    <div key={`veh-row-${v.id}-${row.label}`} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{row.label}</span>
                      <span className="font-semibold text-dark">{row.value}</span>
                    </div>
                  ))}
                </div>
                <Link href="/book-appointment" className="btn-outline w-full justify-center text-sm py-2.5">
                  Book Wash for This Car
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PAYMENTS TAB */}
      {activeTab === 'payments' && (
        <div className="bg-white rounded-2xl border border-sky-100 shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-sky-50">
            <h2 className="font-bold text-dark">Payment History</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-sky-50/60">
                  {['Date', 'Reference', 'Method', 'Status', 'Amount'].map((h) => (
                    <th key={`pay-h-${h}`} className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((pay, i) => (
                  <tr key={pay.id} className={`hover:bg-sky-50/50 transition-colors ${i < paymentHistory.length - 1 ? 'border-b border-sky-50' : ''}`}>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{pay.date}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{pay.ref}</td>
                    <td className="px-4 py-3 text-slate-700">{pay.method}</td>
                    <td className="px-4 py-3">
                      <span className="badge badge-paid">Paid</span>
                    </td>
                    <td className="px-4 py-3 font-extrabold text-primary tabular-nums">{pay.amount} XCG</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-4 border-t border-sky-50 flex justify-between items-center bg-sky-50/30">
            <span className="text-sm text-muted-foreground">Total paid (last 5 transactions)</span>
            <span className="font-extrabold text-primary tabular-nums">
              {paymentHistory.reduce((sum, p) => sum + p.amount, 0)} XCG
            </span>
          </div>
        </div>
      )}

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-sky-100 flex items-center justify-around px-2 py-2 z-40">
        {[
          { label: 'Home', icon: ClipboardList, tab: 'overview' as const },
          { label: 'Book', icon: Calendar, href: '/book-appointment' },
          { label: 'Vehicles', icon: Car, tab: 'vehicles' as const },
          { label: 'Loyalty', icon: Gift, tab: 'overview' as const },
        ].map((item) => (
          <button
            key={`bottom-nav-${item.label}`}
            onClick={() => {
              if ('tab' in item && item.tab) setActiveTab(item.tab);
            }}
            className="flex flex-col items-center gap-1 px-3 py-1 rounded-xl text-muted-foreground hover:text-primary transition-colors"
          >
            <item.icon size={20} />
            <span className="text-xs font-semibold">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

function LayoutDashboard2(props: { size?: number; className?: string }) {
  return <ClipboardList {...props} />;
}